import React, { useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import { setIsResourceFullscreenOpen } from 'store/reducers/navbar-reducer';

import DraftBanner from '../CommonStyled/DraftBanner';
import { IResource } from '../Resources/ResourceCard';
import useResourceDetail from '../Resources/hooks/useResourceDetail';
import useResourcePreviewUrl from '../Resources/hooks/useResourcePreviewUrl';
import {
  ResourceFormat,
  resourceDetailPath,
  resolveResourceViewBehavior,
} from '../Resources/resourceClickBehavior';
import { CloseIcon } from '../shared/icons';
import LoadingComponent from '../shared/loading';
import {
  CompanyResourceClose,
  CompanyResourceErrorText,
  CompanyResourceErrorTitle,
  CompanyResourceErrorWrapper,
  CompanyResourceFooterHider,
  CompanyResourceFullscreen,
  CompanyResourceImage,
  CompanyResourceObject,
} from './styled';

interface CompanyResourcesProps {
  resourceSlug: string;
  existingResource?: IResource;
  onClose?: () => void;
}

export default function CompanyResource({
  resourceSlug,
  existingResource,
  onClose,
}: CompanyResourcesProps) {
  const query = useResourceDetail(resourceSlug, { enabled: !existingResource });
  const data = existingResource || query.data;
  const isError = !existingResource && query.isError;
  const isPdf = data?.resource_format_name === ResourceFormat.PDF;
  const previewUrl = useResourcePreviewUrl(data, {
    loading: !existingResource && isPdf && query.isFetching,
    error: isError,
  });
  const isLoading =
    !existingResource && (query.isLoading || (isPdf && query.isFetching && !previewUrl));
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const openerRef = useRef<HTMLElement | null>(
    document.activeElement instanceof HTMLElement ? document.activeElement : null
  );
  const isEmbeddableHtml = data?.resource_format_name === ResourceFormat.EMBEDDABLE_HTML;
  const viewBehavior = resolveResourceViewBehavior(data || {});

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
      if (onClose && openerRef.current?.isConnected) openerRef.current.focus();
    };
  }, []);

  useEffect(() => {
    dispatch(setIsResourceFullscreenOpen(true));

    return () => {
      dispatch(setIsResourceFullscreenOpen(false));
    };
  }, [dispatch]);

  useEffect(() => {
    // Embeddable HTML fullscreen can't be dismissed with Escape.
    if (!onClose || isEmbeddableHtml) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, isEmbeddableHtml]);

  const shouldShowFooterHider = useMemo(
    () => !isLoading && data?.resource_format_name === ResourceFormat.GOOGLE_STUDIO,
    [data, isLoading]
  );

  const handleClose = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (onClose) {
      onClose();
      return;
    }

    if (new URLSearchParams(location.search).get('resource_tab') === '1') {
      window.close();
      if (window.closed) return;
    }

    history.replace(resourceDetailPath(resourceSlug), {
      suppressAutoFullScreen: true,
    });
  };

  const unavailable = isError || (!isLoading && (!data || !viewBehavior.hasEmbeddableContent));

  const fullScreen = (
    <CompanyResourceFullscreen $overlay={!!onClose} data-resource-fullscreen>
      {/* Embeddable HTML fullscreen has no close button. */}
      {onClose && !isLoading && !isEmbeddableHtml ? (
        <CompanyResourceClose
          type="button"
          onClick={handleClose}
          aria-label="Close full screen"
          autoFocus
        >
          <CloseIcon fill="var(--color-primary)" width={16} height={16} />
        </CompanyResourceClose>
      ) : null}

      {isLoading ? <LoadingComponent idParticles="graph-page-particles" messages={[]} /> : null}

      {unavailable ? (
        <CompanyResourceErrorWrapper>
          <CompanyResourceErrorTitle>Resource unavailable</CompanyResourceErrorTitle>
          <CompanyResourceErrorText>
            This resource does not have content available to display.
          </CompanyResourceErrorText>
        </CompanyResourceErrorWrapper>
      ) : null}

      {!isLoading && !unavailable && data ? (
        <>
          <DraftBanner itemKey={data.uuid} draft={data.status === 'draft'} />
          {data.resource_format_name === ResourceFormat.IMAGE ? (
            <CompanyResourceImage src={data.image} alt={data.title} />
          ) : (
            <CompanyResourceObject
              src={isEmbeddableHtml ? undefined : previewUrl}
              srcDoc={isEmbeddableHtml ? data.content_html : undefined}
              $whiteBackgroundFallback={isEmbeddableHtml}
              sandbox={
                isEmbeddableHtml
                  ? 'allow-scripts allow-modals allow-downloads allow-popups allow-popups-to-escape-sandbox allow-same-origin allow-popups allow-presentation'
                  : undefined
              }
              allow={
                isEmbeddableHtml
                  ? 'autoplay; fullscreen; picture-in-picture'
                  : undefined
              }
              title={data.title}
              allowFullScreen
            />
          )}
          {shouldShowFooterHider ? <CompanyResourceFooterHider /> : null}
        </>
      ) : null}
    </CompanyResourceFullscreen>
  );

  return onClose ? ReactDOM.createPortal(fullScreen, document.body) : fullScreen;
}
